/**
 * Unified search bar — searches countries, cities, and airports.
 *
 * Result ordering:
 *   1. Countries  (exact ISO / name match → highest)
 *   2. Cities     (city-centre coordinates, always above airports for same query)
 *   3. Airports   (sorted within each rank bucket by type: large → medium → small → heliport)
 *
 * Searchable fields:
 *   Country → name, iso2, iso3, continent
 *   City    → city name (deduplicated from airports data)
 *   Airport → ICAO code, IATA code, name, city
 *
 * Arrow keys + Enter for keyboard navigation.
 */
import type { CountryData } from '../types';
import { STATUS_CONFIG } from '../types';
import { fetchJSON } from '../utils/fetch-data';

// ── Types ────────────────────────────────────────────────────────────────────

interface AirportItem {
  name: string;
  icao: string;
  iata: string;
  city: string;
  type: string;
  country: string;
  region: string;   // ISO region code (e.g., "US-WA", "CA-BC")
  lng: number;
  lat: number;
}

interface CityItem {
  name: string;
  country: string;   // ISO-2 from airports data
  region: string;    // ISO region code (e.g., "US-WA")
  lat: number;
  lng: number;
}

interface SearchResult {
  kind: 'country' | 'city' | 'airport';
  label: string;
  sublabel: string;
  country?: CountryData;
  city?: CityItem;
  airport?: AirportItem;
  /** 0 = best match (exact), 1 = prefix, 2 = substring */
  rank: number;
  /** Airport type priority: 0=large, 1=medium, 2=small, 3=heliport, 4=other; 0 for non-airports */
  typePriority: number;
}

type CountrySelectCallback = (country: CountryData) => void;
type AirportSelectCallback = (lng: number, lat: number, name: string) => void;

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_PRIORITY: Record<string, number> = {
  large_airport:  0,
  medium_airport: 1,
  small_airport:  2,
  heliport:       3,
};

const TYPE_LABELS: Record<string, string> = {
  large_airport:  'Large Airport',
  medium_airport: 'Medium Airport',
  small_airport:  'Small Airport',
  heliport:       'Heliport',
};

/** Kind render order: country < city < airport */
const KIND_ORDER: Record<string, number> = {
  country: 0,
  city:    1,
  airport: 2,
};

// ── State ────────────────────────────────────────────────────────────────────

let countries: CountryData[] = [];
let airports:  AirportItem[] = [];
let cities:    CityItem[]    = [];
/** iso2 → CountryData, for enriching city sublabels */
let countryByIso2: Map<string, CountryData> = new Map();

let filteredResults: SearchResult[] = [];
let activeIndex = -1;
let onCountrySelect: CountrySelectCallback;
let onAirportSelect: AirportSelectCallback;

// ── Airport data loader ──────────────────────────────────────────────────────

async function loadAirports(): Promise<AirportItem[]> {
  try {
    const geojson = await fetchJSON<any>('/data/airports.json');
    return geojson.features.map((f: any) => ({
      name:    f.properties.name    || '',
      icao:    f.properties.icao    || '',
      iata:    f.properties.iata    || '',
      city:    f.properties.city    || '',
      type:    f.properties.type    || '',
      country: f.properties.country || '',
      region:  f.properties.region  || '',
      lng:     f.geometry.coordinates[0],
      lat:     f.geometry.coordinates[1],
    }));
  } catch {
    console.warn('Could not load airports for search');
    return [];
  }
}

/**
 * Build a deduplicated city index — one entry per (city, country) pair.
 * The most prominent airport in each city group supplies the representative
 * coordinates (large > medium > small > heliport > other).
 */
function buildCityIndex(airportList: AirportItem[]): CityItem[] {
  // Group by normalised city + country key
  const groups = new Map<string, AirportItem[]>();
  for (const a of airportList) {
    if (!a.city) continue;
    const key = `${a.city.toLowerCase()}|${a.country.toLowerCase()}`;
    let group = groups.get(key);
    if (!group) { group = []; groups.set(key, group); }
    group.push(a);
  }

  const result: CityItem[] = [];
  for (const group of groups.values()) {
    // Sort: most prominent airport first → use its coords as city centre
    group.sort((a, b) => (TYPE_PRIORITY[a.type] ?? 4) - (TYPE_PRIORITY[b.type] ?? 4));
    const best = group[0];
    result.push({
      name:    best.city,
      country: best.country,
      region:  best.region,
      lat:     best.lat,
      lng:     best.lng,
    });
  }
  return result;
}

// ── Search logic ─────────────────────────────────────────────────────────────

function searchAll(query: string): SearchResult[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];

  const results: SearchResult[] = [];

  // ── Country matches ────────────────────────────────────────────────────────
  for (const c of countries) {
    let rank = -1;
    const nameL = c.name.toLowerCase();
    if      (nameL.startsWith(q))              rank = 0;
    else if (nameL.includes(q))                rank = 1;
    else if (c.iso2.toLowerCase() === q)       rank = 0;
    else if (c.iso3.toLowerCase() === q)       rank = 0;
    else if (c.continent.toLowerCase().includes(q)) rank = 2;

    if (rank >= 0) {
      const status = STATUS_CONFIG[c.regulation_status];
      results.push({
        kind: 'country', label: `${c.flag} ${c.name}`,
        sublabel: status.label, country: c,
        rank, typePriority: 0,
      });
    }
  }

  // Airport & city searches only kick in at ≥ 2 chars
  if (q.length < 2) return results.slice(0, 12);

  // ── City matches ───────────────────────────────────────────────────────────
  for (const c of cities) {
    const nameL = c.name.toLowerCase();
    let rank = -1;
    if      (nameL === q)              rank = 0;
    else if (nameL.startsWith(q))     rank = 1;
    else if (nameL.includes(q))       rank = 2;

    if (rank >= 0) {
      // Enrich with flag + country name if we can look it up
      const cd = countryByIso2.get(c.country.toLowerCase());
      const sublabel = cd ? `${cd.flag} ${cd.name}` : c.country;
      // For US/CA cities, append state/province abbreviation (e.g., "Seattle, WA")
      const regionSuffix = c.region && (c.country === 'US' || c.country === 'CA')
        ? `, ${c.region.split('-')[1] || ''}`
        : '';
      results.push({
        kind: 'city', label: `📍 ${c.name}${regionSuffix}`,
        sublabel, city: c,
        rank, typePriority: 0,
      });
    }
  }

  // ── Airport matches ────────────────────────────────────────────────────────
  for (const a of airports) {
    let rank = -1;
    const icaoL = a.icao.toLowerCase();
    const iataL = a.iata.toLowerCase();
    const nameL = a.name.toLowerCase();
    const cityL = a.city.toLowerCase();

    // Exact code match = highest priority
    if      (icaoL === q || iataL === q)                          rank = 0;
    // Code prefix
    else if (icaoL.startsWith(q) || iataL.startsWith(q))         rank = 1;
    // Name / city prefix
    else if (nameL.startsWith(q) || cityL.startsWith(q))         rank = 1;
    // Substring
    else if (nameL.includes(q) || cityL.includes(q))             rank = 2;

    if (rank >= 0) {
      const typePriority = TYPE_PRIORITY[a.type] ?? 4;
      const codes        = a.iata ? `${a.icao} / ${a.iata}` : a.icao;
      const typeLabel    = TYPE_LABELS[a.type] || a.type;
      const cityStr      = a.city ? `${a.city} — ` : '';
      results.push({
        kind: 'airport', label: `✈ ${cityStr}${a.name}`,
        sublabel: `${codes} · ${typeLabel}`, airport: a,
        rank, typePriority,
      });
    }
  }

  // ── Sort ───────────────────────────────────────────────────────────────────
  // Combined score: rank × 10 + typePriority  (airports within same rank
  // bucket are ordered large → medium → small → heliport)
  // Ties broken by kind order (country < city < airport) then alphabetical.
  results.sort((a, b) => {
    const aScore = a.rank * 10 + a.typePriority;
    const bScore = b.rank * 10 + b.typePriority;
    if (aScore !== bScore) return aScore - bScore;
    const kDiff = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
    if (kDiff !== 0) return kDiff;
    return a.label.localeCompare(b.label);
  });

  return results.slice(0, 14);
}

// ── Init ─────────────────────────────────────────────────────────────────────

export function initSearch(
  allCountries: CountryData[],
  countrySelect: CountrySelectCallback,
  airportSelect?: AirportSelectCallback,
) {
  countries = allCountries;
  onCountrySelect = countrySelect;
  onAirportSelect = airportSelect || (() => {});

  // Build ISO-2 → CountryData lookup for city sublabels
  countryByIso2 = new Map(allCountries.map(c => [c.iso2.toLowerCase(), c]));

  // Load airports + build city index asynchronously
  loadAirports().then(items => {
    airports = items;
    cities   = buildCityIndex(items);
    console.log(`Search: indexed ${airports.length} airports, ${cities.length} cities`);
  });

  const container = document.getElementById('search-container')!;
  container.innerHTML = `
    <div class="search-wrapper">
      <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input
        type="text"
        class="search-input"
        placeholder="Search countries, cities, airports (ICAO/IATA)..."
        autocomplete="off"
        spellcheck="false"
      />
      <button class="search-clear" aria-label="Clear search">&times;</button>
      <div class="search-results"></div>
    </div>
  `;

  const input     = container.querySelector('.search-input')  as HTMLInputElement;
  const clearBtn  = container.querySelector('.search-clear')  as HTMLButtonElement;
  const resultsEl = container.querySelector('.search-results') as HTMLDivElement;

  input.addEventListener('input', () => {
    const query = input.value.trim();
    clearBtn.classList.toggle('visible', query.length > 0);

    if (query.length === 0) {
      hideResults(resultsEl);
      return;
    }

    filteredResults = searchAll(query);
    activeIndex = -1;
    renderResults(resultsEl, input);
  });

  input.addEventListener('keydown', (e) => {
    if (!resultsEl.classList.contains('visible')) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = Math.min(activeIndex + 1, filteredResults.length - 1);
      updateActiveResult(resultsEl);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = Math.max(activeIndex - 1, -1);
      updateActiveResult(resultsEl);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < filteredResults.length) {
        pickResult(filteredResults[activeIndex], input, resultsEl);
      } else if (filteredResults.length === 1) {
        pickResult(filteredResults[0], input, resultsEl);
      }
    } else if (e.key === 'Escape') {
      hideResults(resultsEl);
      input.blur();
    }
  });

  clearBtn.addEventListener('click', () => {
    input.value = '';
    clearBtn.classList.remove('visible');
    hideResults(resultsEl);
    input.focus();
  });

  document.addEventListener('click', (e) => {
    if (!container.contains(e.target as Node)) {
      hideResults(resultsEl);
    }
  });
}

// ── Render ────────────────────────────────────────────────────────────────────

function renderResults(resultsEl: HTMLDivElement, input: HTMLInputElement) {
  if (filteredResults.length === 0) {
    resultsEl.innerHTML = '<div class="search-no-results">No results found</div>';
    resultsEl.classList.add('visible');
    return;
  }

  resultsEl.innerHTML = filteredResults.map((r, i) => {
    const active = i === activeIndex ? ' active' : '';

    if (r.kind === 'country') {
      const status = STATUS_CONFIG[r.country!.regulation_status];
      return `
        <div class="search-result-item${active}" data-index="${i}">
          <span class="search-result-flag">${r.country!.flag}</span>
          <span class="search-result-name">${r.country!.name}</span>
          <span class="search-result-status" style="background:${status.color}" title="${status.label}"></span>
        </div>`;

    } else if (r.kind === 'city') {
      return `
        <div class="search-result-item search-result-city${active}" data-index="${i}">
          <span class="search-result-name">${r.label}</span>
          <span class="search-result-meta">${r.sublabel}</span>
        </div>`;

    } else {
      return `
        <div class="search-result-item search-result-airport${active}" data-index="${i}">
          <span class="search-result-name">${r.label}</span>
          <span class="search-result-meta">${r.sublabel}</span>
        </div>`;
    }
  }).join('');

  resultsEl.classList.add('visible');

  resultsEl.querySelectorAll('.search-result-item').forEach((item) => {
    item.addEventListener('click', () => {
      const idx = parseInt((item as HTMLElement).dataset.index || '0');
      pickResult(filteredResults[idx], input, resultsEl);
    });
  });
}

function updateActiveResult(resultsEl: HTMLDivElement) {
  resultsEl.querySelectorAll('.search-result-item').forEach((item, i) => {
    item.classList.toggle('active', i === activeIndex);
  });
}

function pickResult(result: SearchResult, input: HTMLInputElement, resultsEl: HTMLDivElement) {
  if (result.kind === 'country' && result.country) {
    input.value = result.country.name;
    hideResults(resultsEl);
    onCountrySelect(result.country);

  } else if (result.kind === 'city' && result.city) {
    const c = result.city;
    input.value = c.name;
    hideResults(resultsEl);
    onAirportSelect(c.lng, c.lat, c.name);

  } else if (result.kind === 'airport' && result.airport) {
    const a = result.airport;
    input.value = a.name;
    hideResults(resultsEl);
    onAirportSelect(a.lng, a.lat, a.name);
  }
}

function hideResults(resultsEl: HTMLDivElement) {
  resultsEl.classList.remove('visible');
  filteredResults = [];
  activeIndex = -1;
}
